"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Zap, Shield, Server, Crown, User, Activity, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from '@/components/common/Link';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  icon: Zap,
  title: "Systematic Execution",
  description: "Trades are driven by a rules-based strategy process designed to maintain execution logic in under 100ms."
}, {
  icon: Shield,
  title: "Dynamic Risk Allocation",
  description: "Automated position sizing adjusts to your specific account equity and predefined risk parameters in real-time."
}, {
  icon: Server,
  title: "Professional Infrastructure",
  description: "Built on enterprise-grade servers with 99.99% uptime, guaranteeing the algorithms is always working."
}];
const followers = [{
  id: 1,
  x: 20,
  delay: "0ms"
}, {
  id: 2,
  x: 50,
  delay: "150ms"
}, {
  id: 3,
  x: 80,
  delay: "300ms"
}];
export default function TheCopytradingEdge() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: graphicRef,
    isVisible: isGraphicVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="the-copytrading-edge" className="dark bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
            {}
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] md:w-[800px] md:h-[800px] bg-primary/10 rounded-full blur-[100px] md:blur-[150px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center justify-center">
                    
                    {}
                    <div ref={textRef} className={`flex flex-col space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <div className="space-y-4">
                            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                                <Activity className="w-4 h-4" />
                                <span>The Imperium Engine</span>
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                                Technical <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">Superiority</span>
                            </h2>
                            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">Imperium Technologies is built around a structured strategy engine designed for low-latency execution, controlled risk logic, and consistent operational flow. Our focus is on rules-based methodology and disciplined system design rather than emotional or discretionary decision-making.</p>
                        </div>

                        <div className="space-y-6 pt-4">
                            {features.map((feature, i) => <div key={i} data-index={i} className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-card border border-border flex items-center justify-center shadow-sm">
                                        <feature.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-foreground mb-1">{feature.title}</h3>
                                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                                    </div>
                                </div>)}
                        </div>

                        <div className="pt-6">
                            <Link to="#">
                                
                            </Link>
                        </div>
                    </div>

                    {}
                    <div ref={graphicRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isGraphicVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        
                    </div>

                </div>
            </div>
        </section>;
}